import { useEffect, useMemo, useState } from 'react';

import { Button, Icon, Modal, SingleSelectField, Text, TextAreaField } from '../../common/components';
import { t } from '../../common/i18n';
import { formatDateTime } from '../../common/utils/Date/Date';
import { statusOptions } from '../config/issueConfig';
import { Issue, IssueStatus } from '../types';
import IssuePhotoGallery from './IssuePhotoGallery';
import IssueStatusBadge from './IssueStatusBadge';

const namespace = 'Issues';

interface IssueDetailModalProps {
  isShown: boolean;
  issue: Issue | null;
  isAdmin: boolean;
  onHide: () => void;
  onEdit: (issue: Issue) => void;
  onDelete: (issue: Issue) => void;
  onChangeStatus: (issue: Issue, status: IssueStatus) => Promise<void>;
  onAddComment: (issue: Issue, content: string) => Promise<void>;
}

export const IssueDetailModal: React.FC<IssueDetailModalProps> = ({
  isShown,
  issue,
  isAdmin,
  onHide,
  onEdit,
  onDelete,
  onChangeStatus,
  onAddComment,
}) => {
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  useEffect(() => {
    if (isShown) setCommentText('');
  }, [isShown, issue?.id]);

  const statuses = useMemo(() => statusOptions(), []);

  if (!issue) return null;

  const statusValue = statuses.find((option) => option.value === issue.status) ?? null;

  const handleStatusChange = async (status: IssueStatus | null) => {
    if (!status || status === issue.status) return;
    setIsSavingStatus(true);
    try {
      await onChangeStatus(issue, status);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setIsSavingComment(true);
    try {
      await onAddComment(issue, commentText.trim());
      setCommentText('');
    } finally {
      setIsSavingComment(false);
    }
  };

  return (
    <Modal isShown={isShown} onHide={onHide}>
      <div className="IssueDetail">
        <div className="IssueDetail__header">
          <Text size="h2" className="IssueDetail__heading">
            {issue.title}
          </Text>
          <IssueStatusBadge status={issue.status} />
        </div>

        <Text as="div" size="body-small" color="Neutral600" className="mb-2">
          {issue.category.name} <span className="IssueDetail__dot">·</span> {issue.authorName}{' '}
          <span className="IssueDetail__dot">·</span> {formatDateTime(issue.createdAt)}
        </Text>

        {issue.location && (
          <Text as="div" size="body-small" color="Neutral600" className="mb-2">
            {t('Detail.location', namespace)}: {issue.location}
          </Text>
        )}

        <Text as="div" size="body-medium" color="Neutral700" className="IssueDetail__description mb-3">
          {issue.description}
        </Text>

        <IssuePhotoGallery issueId={issue.id} photos={issue.photos} />

        {issue.isModifiable && (
          <div className="IssueDetail__ownActions mb-3">
            <Button
              size="small"
              variant="primary-light"
              iconBefore="edit"
              title={t('Detail.edit', namespace)}
              onClick={() => onEdit(issue)}
            />
            <Button
              size="small"
              variant="primary-light"
              iconBefore="trash"
              title={t('Detail.delete', namespace)}
              onClick={() => onDelete(issue)}
            />
          </div>
        )}

        <div className="IssueDetail__section">
          <Text size="h3" className="mb-1">
            {t('Detail.comments', namespace)}
          </Text>

          {issue.comments.length === 0 && (
            <Text as="div" size="body-small" color="Neutral600" className="mb-2">
              {t('Detail.noComments', namespace)}
            </Text>
          )}

          {issue.comments.map((comment) => (
            <div key={comment.id} className="IssueComment">
              <Text as="div" size="body-medium" color="Neutral700" className="IssueComment__content">
                {comment.content}
              </Text>
              <Text as="div" size="body-small" color="Neutral600">
                {comment.authorName} <span className="IssueDetail__dot">·</span> {formatDateTime(comment.createdAt)}
              </Text>
            </div>
          ))}

          {isAdmin && (
            <div className="IssueDetail__commentForm">
              <TextAreaField
                label={t('Detail.commentLabel', namespace)}
                placeholder={t('Detail.commentPlaceholder', namespace)}
                name="comment"
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                className="mb-2"
              />
              <Button
                size="small"
                title={t('Detail.commentSubmit', namespace)}
                loading={isSavingComment}
                disabledDeps={['demo', 'expired']}
                onClick={handleAddComment}
              />
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="IssueDetail__section">
            <Text size="h3" className="mb-1">
              {t('Detail.statusLabel', namespace)}
            </Text>
            <SingleSelectField
              name="status"
              value={statusValue}
              onChange={(option) => handleStatusChange((option as { value: IssueStatus } | null)?.value ?? null)}
              options={statuses}
              isDisabled={isSavingStatus}
              className="mb-2"
            />
          </div>
        )}

        <div className="IssueDetail__section">
          <Text size="h3" className="mb-1">
            {t('Detail.history', namespace)}
          </Text>

          <div className="IssueHistory__item">
            <Icon name="plus" className="IssueHistory__icon" />
            <Text as="div" size="body-small" color="Neutral700">
              {t('Detail.reported', namespace)}
              <Text as="span" size="body-small" color="Neutral600">
                {' '}
                ({issue.authorName} <span className="IssueDetail__dot">·</span> {formatDateTime(issue.createdAt)})
              </Text>
            </Text>
          </div>

          {issue.statusChanges.map((change) => (
            <div key={change.id} className="IssueHistory__item">
              <Icon name="arrowRightSmall" className="IssueHistory__icon" />
              <Text as="div" size="body-small" color="Neutral700">
                {t(`status.${change.fromStatus}`, namespace)} → {t(`status.${change.toStatus}`, namespace)}
                <Text as="span" size="body-small" color="Neutral600">
                  {' '}
                  ({change.changedByName} <span className="IssueDetail__dot">·</span>{' '}
                  {formatDateTime(change.createdAt)})
                </Text>
              </Text>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default IssueDetailModal;
